import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { TextLoader } from 'components/apiHandling/TextLoader';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import ResponsiveDialog from 'components/dialogs/ResponsiveDialog';
import QueryViewer from 'components/queries/QueryViewer';
import APIRequest from 'system/APIRequest';
import { Query } from 'system/query/Query';
import { QueryInfo } from 'system/query/QueryInfo';

export interface AIQueryBuilderDialogProps {
  queryInfo?: QueryInfo;
  onReplaceQuery: (query: Query) => void;
  onInsertQuery: (query: Query) => void;
  onClose: () => void;
}

export function AIQueryBuilderDialog(props: AIQueryBuilderDialogProps) {
  const { i18n } = useTranslation();

  const [prompt, setPrompt] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [queryResult, setQueryResult] = useState<Query | undefined>(undefined);

  function runAIQuery() {
    setQueryResult(undefined);
    setIsLoading(true);

    APIRequest(`query/ai-assist`, {
      prompt: prompt,
    }).then((response: any) => {
      setIsLoading(false);
      const newQuery = new Query({
        dataset_id : props.queryInfo?.dataset?.id,
        query:response,
        prompt:prompt
      });
      setQueryResult(newQuery);
    }).catch((error: APIError) => {
      setIsLoading(false);
      setErrorMessage(error.message);
    });
  }

  return (
    <>
    <ResponsiveDialog
      open={!queryResult}
      onClose={props.onClose}
      maxWidth="md"
      disableClickaway={true}
    >
      <DialogTitle>{i18n.t("ai-query.dialog-title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {i18n.t("ai-query.prompt-description")}
        </Typography>
        <TextField
          fullWidth
          autoFocus
          autoComplete="off"
          disabled={isLoading}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)} />
        {isLoading &&
          <Box sx={{textAlign: "center", mt:2}}>
            <TextLoader />
          </Box>
        }
        {errorMessage &&
          <Alert severity="error" sx={{mt:2}}>
            {errorMessage}
          </Alert>
        }
      </DialogContent>
      <DialogActions sx={{ padding: "0 18px 18px 0" }} >
        <Button
          autoFocus
          size="small"
          variant="contained"
          color="secondary"
          onClick={props.onClose} >
          { i18n.t("cancel") }
        </Button>
        <Button
          variant="contained"
          color='success'
          size="small"
          disabled={!prompt || isLoading}
          onClick={() => {
            runAIQuery();
          }} >
          { i18n.t("submit") }
        </Button>
      </DialogActions>
    </ResponsiveDialog>
    {!!queryResult && props.queryInfo &&
    <ConfirmationDialog
      isOpen={!!queryResult}
      confirmText={i18n.t("insert")}
      onClose={(confirmed) => {
        if (!confirmed) {
          setQueryResult(undefined);
          setIsLoading(false);
          setErrorMessage(undefined);
        }
        if (confirmed) {
          props.onInsertQuery(queryResult!);
        }
      }}
      title={i18n.t("ai-query.confirmation")}
      additionalActionButtons={
        <Button
          variant="contained"
          color='success'
          size="small"
          onClick={() => {
            if (queryResult) {
              props.onReplaceQuery(queryResult);
            }
          }} >
          { i18n.t("replace") }
        </Button>
      }>
        <QueryViewer query={queryResult} queryInfo={props.queryInfo} />
      </ConfirmationDialog>
      }
    </>
);
}